import { useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";

import StatTile from "shell/StatTile";
import WeeklyAttendanceChart from "shell/WeeklyAttendanceChart";

/**
 * Staff dashboard — day-to-day operational view (members, attendance, SMS),
 * mirroring AdminDashboard's real-data pattern minus financial reporting.
 */
export default function StaffDashboard() {
  const theme = useTheme();
  const { church, user } = useAuth();
  const [stats, setStats] = useState({ members: { total: 0 }, attendance: { today: 0 } });
  const [sms, setSms] = useState({ balance: 0, totalSent: 0 });
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);

  useEffect(() => {
    if (!church?.id) return;
    const token = localStorage.getItem("authToken");
    const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE}/analytics/members-count?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/attendance-count?church_id=${church.id}`, { headers }).then(
        (r) => r.json()
      ),
    ])
      .then(([m, a]) =>
        setStats({
          members: { total: m?.total ?? 0 },
          attendance: { today: a?.today ?? 0 },
        })
      )
      .catch(() => {});

    fetch(`${API_BASE}/analytics/weekly-attendance?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((rows) => setWeeklyAttendance(Array.isArray(rows) ? rows : []))
      .catch(() => {});

    fetch(`${API_BASE}/v3/sms/balance?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((j) => {
        const d = j?.data || {};
        setSms({ balance: d.balance ?? 0, totalSent: d.total_sent ?? 0 });
      })
      .catch(() => {});
  }, [church?.id]);

  const greeting = greetingFor(new Date());

  return (
    <Box>
      <Card
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          background: `linear-gradient(135deg, ${theme.palette.teal[600]} 0%, ${theme.palette.teal[400]} 100%)`,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <Typography variant="overline" sx={{ opacity: 0.9, color: "white" }}>
          {greeting}, {firstName(user?.name)}
        </Typography>
        <Typography variant="h2" sx={{ color: "white", mt: 0.5 }}>
          {church?.name || "Your Church"}
        </Typography>
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            icon={<Icon sx={{ color: "white !important", fontSize: 16 }}>group</Icon>}
            label={`${stats.members.total.toLocaleString()} members`}
            sx={pillSx}
          />
          <Chip
            icon={<Icon sx={{ color: "white !important", fontSize: 16 }}>sms</Icon>}
            label={`${Number(sms.balance).toLocaleString()} SMS credits`}
            sx={pillSx}
          />
        </Box>
      </Card>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatTile
            icon="group"
            label="Total Members"
            value={stats.members.total.toLocaleString()}
            sub="across all departments"
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatTile
            icon="how_to_reg"
            label="Attendance today"
            value={stats.attendance.today.toLocaleString()}
            sub="checked-in this service"
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatTile
            icon="sms"
            label="SMS Credits"
            value={Number(sms.balance).toLocaleString()}
            sub={`${sms.totalSent.toLocaleString()} sent`}
            tone={sms.balance < 50 ? "warning" : "purple"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h5">Weekly attendance</Typography>
              <Chip
                size="small"
                label="Last 7 days"
                sx={{ bgcolor: theme.palette.ink[100], color: "text.secondary" }}
              />
            </Box>
            <WeeklyAttendanceChart data={weeklyAttendance} />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

const pillSx = {
  bgcolor: "rgba(255,255,255,0.18)",
  color: "white",
  fontWeight: 600,
  border: "1px solid rgba(255,255,255,0.18)",
  "& .MuiChip-icon": { ml: 0.5 },
};

function greetingFor(d) {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
function firstName(name) {
  return (name || "there").split(" ")[0];
}
